import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Confirmation, Field, Icon, Text } from 'admination-react'
import { SignaturePad } from '@/core/components/ui/signature-pad'
import { formatDatum } from '@/core/lib/dates'
import {
  AUSSERHALB_ZEITRAUM,
  BEREITS_UNTERSCHRIEBEN,
  KEIN_UNTERRICHTSTAG,
  anwesenheitUnterschreiben,
  codePruefen,
  isCodeUnbekannt,
  refusalToken,
  type AnwesenheitKarte,
} from '../../api/anwesenheit'
import {
  MAX_FEHLVERSUCHE,
  entsperren,
  fehlversuchZaehlen,
  teilnehmersichtBeenden,
  useTeilnehmersicht,
} from '../../lib/teilnehmersicht'
import { PasswortPrompt } from './PasswortPrompt'

type Schritt =
  | { kind: 'code' }
  | { kind: 'unterschrift'; code: string; karte: AnwesenheitKarte }
  | { kind: 'bestaetigt'; karte: AnwesenheitKarte }

type Frage = 'verlassen' | 'entsperren' | null

const BESTAETIGT_MS = 5000

/**
 * The reception device: the participant types the four-digit code from
 * their profile, signs today's attendance and the view returns to the code
 * entry for the next person. The coach's password leaves it (ADM Anwesenheit).
 */
export function Teilnehmersicht() {
  const { t } = useTranslation('vergabeStaff')
  const sicht = useTeilnehmersicht()
  const gesperrt = sicht.fehlversuche >= MAX_FEHLVERSUCHE
  const [schritt, setSchritt] = useState<Schritt>({ kind: 'code' })
  const [code, setCode] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [frage, setFrage] = useState<Frage>(null)
  const eingabe = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (schritt.kind === 'code' && !gesperrt && !busy) eingabe.current?.focus()
  }, [schritt.kind, gesperrt, busy])

  useEffect(() => {
    if (schritt.kind !== 'bestaetigt') return
    const timer = window.setTimeout(() => setSchritt({ kind: 'code' }), BESTAETIGT_MS)
    return () => window.clearTimeout(timer)
  }, [schritt])

  function meldung(error: unknown): string {
    const token = refusalToken(error)
    if (token === AUSSERHALB_ZEITRAUM) return t('anwesenheit.ausserhalb')
    if (token === BEREITS_UNTERSCHRIEBEN) return t('anwesenheit.bereits')
    if (token === KEIN_UNTERRICHTSTAG) return token
    if (token) return t('anwesenheit.nichtMoeglich')
    console.error('Anwesenheit:', error)
    return t('anwesenheit.fehler')
  }

  async function pruefen(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (busy) return
    if (code.length !== 4) {
      setFehler(t('anwesenheit.vierZiffern'))
      return
    }
    setBusy(true)
    setFehler(null)
    try {
      const karte = await codePruefen(code)
      setSchritt({ kind: 'unterschrift', code, karte })
    } catch (error) {
      if (isCodeUnbekannt(error)) {
        fehlversuchZaehlen()
        setFehler(t('anwesenheit.codeFalsch', { rest: MAX_FEHLVERSUCHE - sicht.fehlversuche - 1 }))
      } else {
        setFehler(meldung(error))
      }
    } finally {
      setCode('')
      setBusy(false)
    }
  }

  async function unterschreiben(pngDataUrl: string) {
    if (schritt.kind !== 'unterschrift') return
    try {
      const karte = await anwesenheitUnterschreiben(schritt.code, pngDataUrl)
      setSchritt({ kind: 'bestaetigt', karte })
    } catch (error) {
      setFehler(isCodeUnbekannt(error) ? t('anwesenheit.codeFalsch', { rest: MAX_FEHLVERSUCHE - sicht.fehlversuche }) : meldung(error))
      setSchritt({ kind: 'code' })
      throw error
    }
  }

  function karteText(karte: AnwesenheitKarte) {
    return { name: karte.name, datum: formatDatum(karte.datum) }
  }

  return (
    <div className="page" id="page">
      <div className="layout layout--center" id="base-layout">
        {schritt.kind === 'bestaetigt' ? (
          <div className="confirmation-panel">
            <Confirmation tone="success" title={t('anwesenheit.bestaetigtTitel', karteText(schritt.karte))} inline>
              {t('anwesenheit.bestaetigtText', karteText(schritt.karte))}
            </Confirmation>
            <div className="confirmation-actions">
              <Button variant="primary" onClick={() => setSchritt({ kind: 'code' })}>
                <Icon name="i-arrow-right" />{t('anwesenheit.naechste')}
              </Button>
            </div>
          </div>
        ) : gesperrt ? (
          <div className="confirmation-panel">
            <Confirmation tone="warning" title={t('anwesenheit.gesperrtTitel')} inline>
              {t('anwesenheit.gesperrtText')}
            </Confirmation>
            <div className="confirmation-actions">
              <Button variant="primary" onClick={() => setFrage('entsperren')}>
                <Icon name="i-person" />{t('anwesenheit.entsperren')}
              </Button>
              <Button onClick={() => setFrage('verlassen')}>
                <Icon name="i-logout" />{t('anwesenheit.verlassen')}
              </Button>
            </div>
          </div>
        ) : (
          <main className="form form--narrow">
            <div className="section">
              <h3 className="section-header">{t('anwesenheit.titel')}</h3>
              <div className="section-body">
                <p className="section-note">{t('anwesenheit.hinweis')}</p>
                <form onSubmit={(e) => void pruefen(e)} noValidate aria-label={t('anwesenheit.titel')} className="section-body">
                  <Field label={t('anwesenheit.code')} htmlFor="anwesenheit-code">
                    <Text ref={eingabe} id="anwesenheit-code" name="code" size="lg" inputMode="numeric" autoComplete="one-time-code"
                      maxLength={4} required value={code} disabled={busy}
                      onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 4))} />
                  </Field>
                  <Button type="submit" variant="primary" block loading={busy}>
                    <Icon name="i-arrow-right" />{t('anwesenheit.weiter')}
                  </Button>
                </form>
                {fehler && (
                  <p role="alert" className="field-error">
                    {fehler}
                  </p>
                )}
                <div className="section-actions">
                  <Button onClick={() => setFrage('verlassen')} disabled={busy}>
                    <Icon name="i-logout" />{t('anwesenheit.verlassen')}
                  </Button>
                </div>
              </div>
            </div>
          </main>
        )}
      </div>
      <SignaturePad
        open={schritt.kind === 'unterschrift'}
        title={t('anwesenheit.unterschriftTitel')}
        hint={schritt.kind === 'unterschrift' ? t('anwesenheit.unterschriftHinweis', karteText(schritt.karte)) : undefined}
        confirmLabel={t('anwesenheit.unterschreiben')}
        secondaryAction={
          <Button onClick={() => setSchritt({ kind: 'code' })}>
            <Icon name="i-person" />{t('anwesenheit.nichtIch')}
          </Button>
        }
        successMessage={null}
        onSubmit={unterschreiben}
        onClose={() => setSchritt((aktuell) => (aktuell.kind === 'unterschrift' ? { kind: 'code' } : aktuell))}
      />
      {frage && (
        <PasswortPrompt
          titel={frage === 'verlassen' ? t('anwesenheit.verlassen') : t('anwesenheit.entsperren')}
          bestaetigen={frage === 'verlassen' ? t('anwesenheit.verlassen') : t('anwesenheit.entsperren')}
          onBestaetigt={() => {
            setFrage(null)
            if (frage === 'verlassen') teilnehmersichtBeenden()
            else {
              entsperren()
              setFehler(null)
            }
          }}
          onClose={() => setFrage(null)}
        />
      )}
    </div>
  )
}
